<template>
    <div class="relative w-full p-0">
        <div class="relative flex w-full select-none">
            <input
                :id="inputId"
                ref="input"
                :data-cy="vid"
                :disabled="disabled"
                :name="vid"
                type="tel"
                class="peer rounded-muval-1 focus:border-focus block w-full resize-y overflow-y-auto border px-3.5 pl-16 placeholder-transparent focus:ring-0 focus:outline-hidden"
                :class="[getStyles]"
                :placeholder="placeholder ? placeholder : label"
                :value="nationalInput"
                :autocomplete="canAutoComplete"
                @input="onInput"
                @blur="handleBlur"
            />
            <div
                v-if="label"
                class="peer text-muval-gray-3 peer-placeholder-shown:text-muval-gray-3 peer-focus:text-focus pointer-events-none absolute left-2 z-10 line-clamp-1 -translate-y-1/2 text-sm transition-all duration-100 peer-placeholder-shown:top-1/2 peer-placeholder-shown:left-16 peer-placeholder-shown:text-base peer-focus:top-0 peer-focus:left-2 peer-focus:text-sm"
            >
                <span class="px-1">{{ label }}</span>
                <div
                    class="absolute top-2.5 -z-10 h-0.5 w-full"
                    :class="getStyles"
                ></div>
            </div>

            <Listbox
                :modelValue="selectedCode"
                :disabled="disabled"
                as="div"
                class="absolute inset-y-0 left-0 z-30"
                @update:modelValue="onCodeSelect"
            >
                <div class="relative h-full">
                    <ListboxButton
                        class="rounded-l-muval-1 border-muval-gray-4 text-muval-gray-2 focus-visible:text-focus flex h-full cursor-pointer items-center gap-0.5 border-r pr-1 pl-3 focus:outline-hidden"
                    >
                        <span>{{ selectedCode }}</span>
                        <ChevronUpDownIcon
                            class="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                        />
                    </ListboxButton>

                    <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <ListboxOptions
                            class="absolute z-30 mt-1 max-h-60 w-max min-w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
                        >
                            <ListboxOption
                                v-for="code in countryCodes"
                                v-slot="{ active, selected }"
                                :key="code"
                                :value="code"
                                as="template"
                            >
                                <li
                                    :class="[
                                        active ? 'bg-muval-gray-5 text-muval-gray-1' : 'text-gray-900',
                                        'relative cursor-default px-3 py-2 select-none',
                                    ]"
                                    class="mx-1 rounded-md"
                                >
                                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                                        {{ code }}
                                    </span>
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </div>
            </Listbox>

            <div
                v-if="errorMessage"
                class="text-muval-gray-3 pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            >
                <SvgIcon
                    class="h-4 w-4"
                    name="input_error"
                />
            </div>
        </div>
        <div
            class="pointer-events-none absolute -bottom-3.5 left-0 w-full truncate text-xs whitespace-nowrap"
            :class="errorMessage ? 'text-red-500' : ''"
        >
            <span class="ml-2 text-xs">{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { idGen } from '@/utilities/idGen';
import SvgIcon from '../utilities/SvgIcon.vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    autocomplete: {
        type: Boolean,
        required: false,
        default: true,
    },
    countryCodes: {
        type: Array,
        required: false,
        default: () => ['+61', '+64', '+63', '+1', '+44', '+91'],
    },
    defaultCode: {
        type: String,
        required: false,
        default: '+61',
    },
});

const inputId = idGen(6);
const { value: fieldValue, errorMessage, handleBlur } = useField(props.vid, undefined);

const selectedCode = ref(props.defaultCode);
const nationalInput = ref('');

// The E.164 value the field should hold for the current UI state. The leading
// zero of a national number is stripped so "+61" + "0412..." => "+61412...".
// A value carrying its own "+" (pasted international number whose country code
// isn't in the picker) is passed through verbatim — never double-prefixed.
const combined = computed(() => {
    const cleaned = nationalInput.value.replace(/\s+/g, '');
    if (!cleaned) {
        return '';
    }
    if (cleaned.startsWith('+')) {
        return cleaned;
    }
    return `${selectedCode.value}${cleaned.replace(/^0+/, '')}`;
});

// Longest-prefix match so "+64" wins over "+6" style overlaps. Values without a
// recognised code (legacy national numbers like "0412...") display under the
// default code but are never rewritten unless the user edits them.
function parseE164(value) {
    if (!value || typeof value !== 'string') {
        return { code: props.defaultCode, number: '' };
    }

    const codes = [...props.countryCodes].sort((a, b) => b.length - a.length);
    for (const code of codes) {
        if (value.startsWith(code)) {
            return { code, number: value.slice(code.length) };
        }
    }

    return { code: props.defaultCode, number: value };
}

// Sync external writes (resetForm / setValues) into the picker + input. The
// guard skips our own writes so what the user typed is never rewritten.
watch(
    fieldValue,
    (newVal) => {
        if (newVal === combined.value) {
            return;
        }

        const parsed = parseE164(newVal ?? '');
        selectedCode.value = parsed.code;
        nationalInput.value = parsed.number;
    },
    { immediate: true },
);

// A full international number typed or pasted into the text input ("+63 917...")
// must not get the picker's code prepended on top. When its country code is in
// the picker, split it across picker + input; otherwise keep it whole so
// `combined` passes it through verbatim.
function onInput(event) {
    const raw = event.target.value;
    const cleaned = raw.replace(/\s+/g, '');

    if (cleaned.startsWith('+')) {
        const parsed = parseE164(cleaned);
        if (parsed.number !== cleaned) {
            selectedCode.value = parsed.code;
            nationalInput.value = parsed.number;
        } else {
            nationalInput.value = cleaned;
        }
    } else {
        nationalInput.value = raw;
    }

    fieldValue.value = combined.value;
}

function onCodeSelect(code) {
    selectedCode.value = code;
    if (nationalInput.value) {
        fieldValue.value = combined.value;
    }
}

const canAutoComplete = computed(() => {
    if (props.autocomplete) {
        return 'on';
    } else {
        return 'off';
    }
});

const getStyles = computed(() => {
    if (props.disabled) {
        return 'bg-muval-gray-5 border-muval-gray-4 cursor-not-allowed';
    } else if (errorMessage.value) {
        return 'bg-white border-error';
    } else {
        return 'bg-white border-muval-gray-4';
    }
});
</script>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
}
</style>
